import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ICliJob } from 'cli/types';
import { NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { useQuotePartActions } from 'quotes/hooks';
import { mapQuotePartApplyCostDiscount } from 'quotes/mappers';
import { ICostDiscountForm, IQuotePart } from 'quotes/types';

import QuotePartCostDiscountFormView from './QuotePartCostDiscountFormView';
import { quotePartCostDiscountSchema } from './schema';

interface IProps {
  isOpen?: boolean;
  onClose?: (closeSelection?: boolean) => void;
  onSubmitSuccess: () => void;
}

const QuotePartCostDiscountForm = ({ isOpen = false, onClose, onSubmitSuccess }: IProps) => {
  const { t } = useTranslation();
  const { selectedRows, updateRows } = useAgGridClientTableContext();
  const { showSuccessNotification } = useNotificationContext();

  const { handleCliJob } = useCliJobHandler();
  const { applyCostDiscount } = useQuotePartActions();

  const onSubmit = (reset: UseFormReset<ICostDiscountForm>) => async (data: ICostDiscountForm) => {
    handleCliJob(applyCostDiscount, mapQuotePartApplyCostDiscount(selectedRows, data), {
      cliJobInProgressMessage: t('messageApplyCostDiscountInProgress'),
      onCliJobFinished: (cliJob: ICliJob) => {
        const quoteParts = cliJob.result as IQuotePart[];

        updateRows({ update: quoteParts });

        showSuccessNotification(
          { message: t('messageApplyCostDiscountSuccess') },
          NotificationPosition.TOP
        );

        onSubmitSuccess();
      },
    });

    handleOnClose(reset, true);
  };

  const handleOnClose = (reset: UseFormReset<ICostDiscountForm>, closeSelection = false) => {
    onClose(closeSelection);
    reset();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <FormProvider schema={quotePartCostDiscountSchema()}>
      <QuotePartCostDiscountFormView isOpen={isOpen} onClose={handleOnClose} onSubmit={onSubmit} />
    </FormProvider>
  );
};

export default QuotePartCostDiscountForm;
